import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProductContext } from "../../../store";
import { getAiVisibilityReport } from "../../../services/aiVisibilityApi";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import AiVisibilityReportView from "../../../components/AiVisibility/AiVisibilityReportView";
import LoadingState from "../../../components/AiVisibility/Report/LoadingState";
import ErrorState from "../../../components/AiVisibility/Report/ErrorState";
import EmptyState from "../../../components/AiVisibility/Report/EmptyState";
import { parseAuditIdParam } from "../../../utils/auditIdUtils";

const AUDIT_LIST_PATH = "/aivisibility/audit-list";

const AiVisibilityReport = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const auditId = parseAuditIdParam(searchParams.get("auditId"));
	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const previousWebsiteIdRef = useRef(currentWebsiteId);
	const loadRequestRef = useRef(0);

	const goToAuditList = useCallback(() => {
		loadRequestRef.current += 1;
		setReport(null);
		setError(null);
		setLoading(false);
		navigate(AUDIT_LIST_PATH, { replace: true });
	}, [navigate]);

	const loadReport = useCallback(async ({ silent = false } = {}) => {
		if (!currentWebsiteId) {
			setLoading(false);
			return;
		}
		const requestId = ++loadRequestRef.current;
		if (!silent) {
			setLoading(true);
			setError(null);
		}
		const result = await getAiVisibilityReport(currentWebsiteId, auditId);
		if (requestId !== loadRequestRef.current) {
			return;
		}
		if (!silent) {
			setLoading(false);
		}
		if (!result.ok) {
			if (!silent) {
				if (auditId != null) {
					goToAuditList();
					return;
				}
				setError(result.message || "Failed to load report");
				setReport(null);
			}
			return;
		}
		setReport(result.report);
	}, [auditId, currentWebsiteId, goToAuditList]);

	const refreshReport = useCallback(
		(options) => loadReport({ silent: true, ...options }),
		[loadReport]
	);

	useEffect(() => {
		if (!currentWebsiteId) {
			goToAuditList();
			return;
		}

		const storeChanged =
			previousWebsiteIdRef.current != null &&
			previousWebsiteIdRef.current !== currentWebsiteId;

		previousWebsiteIdRef.current = currentWebsiteId;

		if (storeChanged) {
			goToAuditList();
			return;
		}

		loadReport();
	}, [currentWebsiteId, goToAuditList, loadReport]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (loading) {
		return <LoadingState />;
	}

	if (error) {
		return (
			<ErrorState
				message={error}
				onRetry={loadReport}
			/>
		);
	}

	if (!report) {
		return <EmptyState />;
	}

	return (
		<AiVisibilityReportView
			report={report}
			websiteId={currentWebsiteId}
			onReportRefresh={refreshReport}
		/>
	);
};

export default AiVisibilityReport;
